export type InitialRiskMethod = 'amount' | 'stopPrice';

export type StopRiskAssetClass = 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO' | 'OTHER';

export type StopRiskInput = {
  assetClass: StopRiskAssetClass;
  side: 'buy' | 'sell';
  entryPrice: number;
  quantity: number;
  stopPrice: number;
  futuresMultiplier?: number | null;
};

export type StopRiskResult =
  | {
      ok: true;
      perUnitRisk: number;
      totalRisk: number;
    }
  | {
      ok: false;
      reason: string;
    };

export function computeInitialRiskFromStop(input: StopRiskInput): StopRiskResult {
  const entryPrice = Number(input.entryPrice);
  const quantity = Number(input.quantity);
  const stopPrice = Number(input.stopPrice);

  if (!Number.isFinite(entryPrice) || entryPrice <= 0) {
    return { ok: false, reason: 'Entry price is unavailable for this trade.' };
  }
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return { ok: false, reason: 'Opening quantity is unavailable for this trade.' };
  }
  if (!Number.isFinite(stopPrice) || stopPrice <= 0) {
    return { ok: false, reason: 'Enter a valid stop price greater than 0.' };
  }

  const side = input.side;
  let perUnitRisk = 0;

  if (side === 'buy') {
    perUnitRisk = entryPrice - stopPrice;
    if (!(perUnitRisk > 0)) {
      return { ok: false, reason: 'For long trades, stop price must be below entry price.' };
    }
  } else {
    perUnitRisk = stopPrice - entryPrice;
    if (!(perUnitRisk > 0)) {
      return { ok: false, reason: 'For short trades, stop price must be above entry price.' };
    }
  }

  if (input.assetClass === 'STOCKS') {
    const totalRisk = perUnitRisk * quantity;
    if (!(totalRisk > 0) || !Number.isFinite(totalRisk)) {
      return { ok: false, reason: 'Unable to calculate Initial Risk from stop price.' };
    }
    return { ok: true, perUnitRisk, totalRisk };
  }

  if (input.assetClass === 'FUTURES') {
    const multiplier = Number(input.futuresMultiplier);
    if (!Number.isFinite(multiplier) || multiplier <= 0) {
      return { ok: false, reason: 'Futures contract multiplier is unavailable for this symbol.' };
    }
    const totalRisk = perUnitRisk * quantity * multiplier;
    if (!(totalRisk > 0) || !Number.isFinite(totalRisk)) {
      return { ok: false, reason: 'Unable to calculate Initial Risk from stop price.' };
    }
    return { ok: true, perUnitRisk, totalRisk };
  }

  if (input.assetClass === 'FOREX') {
    return { ok: false, reason: 'Stop Price mode is unavailable for Forex due to unit/model ambiguity. Use Risk Amount ($).' };
  }

  if (input.assetClass === 'CRYPTO') {
    return { ok: false, reason: 'Stop Price mode is unavailable for Crypto in the current risk model. Use Risk Amount ($).' };
  }

  return { ok: false, reason: 'Stop Price mode is unavailable for this instrument type. Use Risk Amount ($).' };
}
